import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import {
  ArrowLeft,
  Bookmark,
  BookmarkCheck,
  Briefcase,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clipboard,
  Clock,
  Crown,
  DollarSign,
  ExternalLink,
  FileText,
  Filter,
  Loader2,
  Lock,
  MapPin,
  RefreshCw,
  Search,
  Sparkles,
  X,
  Zap,
} from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  price as formatPrice,
  useMarketConfig,
  usePricingForMarket,
} from "@/utils/marketConfig";
import "@/styles/landing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VIEWS = [
  { value: "discover", label: "Discover" },
  { value: "saved", label: "Saved" },
  { value: "applied", label: "Applied" },
];
const SORT_OPTIONS = [
  { value: "match", label: "Best match" },
  { value: "date", label: "Newest" },
];
const APPLICATION_STATUSES = [
  ["applied", "Applied"],
  ["interviewing", "Interviewing"],
  ["offer", "Offer"],
  ["accepted", "Accepted"],
  ["rejected", "Rejected"],
  ["withdrawn", "Withdrawn"],
];

const parseJobPage = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? Math.min(parsed, 500) : 1;
};

const buildJobSearchParams = ({
  view,
  keywords,
  location,
  sortBy,
  useOptimized,
  page,
}) => {
  const next = new URLSearchParams();
  if (view !== "discover") next.set("view", view);
  if (keywords) next.set("keywords", keywords);
  if (location !== "All Locations") next.set("location", location);
  if (sortBy !== "match") next.set("sort", sortBy);
  if (useOptimized) next.set("source", "optimized");
  if (page > 1) next.set("page", String(page));
  return next;
};

const cleanDescription = (html) =>
  DOMPurify.sanitize(html || "", {
    ALLOWED_TAGS: ["b", "i", "em", "strong", "u", "p", "br", "ul", "ol", "li", "span"],
    ALLOWED_ATTR: [],
  });

const jobDescriptionToPlainText = (value) => {
  const withLineBreaks = String(value || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<li(?:\s[^>]*)?>/gi, "\n• ")
    .replace(/<\/(?:p|div|li|ul|ol|h[1-6])>/gi, "\n");
  const safeText = DOMPurify.sanitize(withLineBreaks, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });
  const decoder = document.createElement("textarea");
  decoder.innerHTML = safeText;
  return decoder.value
    .replace(/\r\n?/g, "\n")
    .replace(/[^\S\n]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
    .slice(0, 100000);
};

const applyKitDescriptionForJob = (job) => (
  job?.personalization_allowed
    ? jobDescriptionToPlainText(job.description)
    : ""
);

const safeExternalUrl = (value) => {
  try {
    const parsed = new URL(value);
    const allowedProtocol = ["http:", "https:"].includes(parsed.protocol);
    return allowedProtocol
      && parsed.hostname
      && !parsed.username
      && !parsed.password
      ? parsed.toString()
      : "";
  } catch {
    return "";
  }
};

const stableJobKey = (job) =>
  String(job?.job_id || job?.canonical_job_id || job?.url || job?._tracking?.id || "");

const jobSignInPath = (searchParams) => {
  const query = searchParams.toString();
  const returnTo = `/jobs${query ? `?${query}` : ""}`;
  return `/auth?return=${encodeURIComponent(returnTo)}`;
};

const formatPostedDate = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(parsed);
};

const requestUuid = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

function extractSkills(text) {
  if (!text) return [];
  const common = [
    "python", "sql", "java", "javascript", "react", "aws", "docker", "kubernetes",
    "tensorflow", "pytorch", "machine learning", "data analysis", "excel", "tableau",
    "power bi", "deep learning", "nlp", "api", "git", "agile", "scrum", "node",
    "c++", "golang", "rust", "spark", "hadoop", "airflow", "mongodb", "postgresql",
  ];
  const lower = text.toLowerCase();
  const containsWholeTerm = (skill) => {
    const escaped = skill.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const plural = skill === "api" ? "s?" : "";
    return new RegExp(`(?:^|[^a-z0-9])${escaped}${plural}(?:$|[^a-z0-9])`, "i").test(lower);
  };
  return common.filter(containsWholeTerm).slice(0, 8);
}

function MatchBadge({ score, basis }) {
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const color = pct >= 75 ? "#16a34a" : pct >= 50 ? "#eab308" : "#ef4444";
  const strength = pct >= 75 ? "Strong" : pct >= 50 ? "Good" : "Fair";
  const basisLabel = basis === "role_and_keywords" ? "role match" : "resume match";
  return (
    <div
      className="flex flex-col items-center gap-1 shrink-0"
      data-testid="match-badge"
      aria-label={`${pct}% ${basisLabel}`}
    >
      <div
        className="relative w-14 h-14 rounded-full grid place-items-center"
        style={{ background: `conic-gradient(${color} ${pct * 3.6}deg, var(--ap-line) 0deg)` }}
      >
        <div className="absolute inset-[3px] rounded-full bg-white grid place-items-center">
          <span className="ap-display text-[13px] font-extrabold" style={{ color }}>{pct}%</span>
        </div>
      </div>
      <span className="text-[9px] font-bold uppercase tracking-wider text-center" style={{ color }}>
        {strength} {basis === "role_and_keywords" ? "role" : "resume"}
      </span>
    </div>
  );
}

function JobCard({
  job,
  view,
  matchBasis,
  isBookmarked,
  bookmarkSaving,
  onBookmark,
  onMarkApplied,
  markingApplied,
  onOpenApplyKit,
  onNativeApply,
  hasSeasonPass,
  user,
  applicationSaving,
  onApplicationStatus,
}) {
  const [expanded, setExpanded] = useState(false);
  const available = job._available !== false;
  const firstParty = job.provider === "placementos" || job.is_first_party || job.is_direct;
  const nativePlacementOSApply = firstParty && job.application_method !== "external";
  const tracking = job._tracking || {};
  const jobKey = stableJobKey(job).replace(/[^a-zA-Z0-9_-]/g, "-");
  const detailId = `job-details-${jobKey}`;
  const destination = available && (!firstParty || job.application_method === "external") ? safeExternalUrl(job.url) : "";
  const attribution = safeExternalUrl(job.attribution_url);
  const status = tracking.status || "applied";

  return (
    <article
      className={`ap-card ap-pop overflow-hidden transition-all ${expanded ? "ring-2 ring-[var(--ap-violet)]/30" : ""}`}
      data-testid={`job-card-${String(job.title || "job").replace(/\s/g, "-").toLowerCase().slice(0, 30)}`}
    >
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <button
            type="button"
            className="flex-1 min-w-0 text-left bg-transparent border-0 p-0 cursor-pointer"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
            aria-controls={detailId}
          >
            <span className="flex items-center gap-2">
              <span className="ap-display text-[15px] font-bold text-[var(--ap-ink)] truncate">
                {job.title || "Untitled role"}
              </span>
              <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </span>
            <span className="block text-[13px] font-medium text-[var(--ap-muted)] mt-0.5 mb-2">
              {job.company || (available ? "Company not listed" : "Listing unavailable")}
            </span>
            <span className="flex items-center gap-3 flex-wrap text-[12px] text-[var(--ap-muted)]">
              {job.locations && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[var(--ap-violet)]" />{job.locations}</span>}
              {job.salary && <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5 text-[var(--ap-green)]" />{job.salary}</span>}
              {job.date && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{formatPostedDate(job.date)}</span>}
              {view === "applied" && tracking.updated_at && (
                <span>Updated {formatPostedDate(tracking.updated_at)}</span>
              )}
            </span>
          </button>

          <div className="flex items-start gap-3 shrink-0">
            {view === "applied" && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-wide text-emerald-700" data-testid="applied-job-state">
                <CheckCircle2 className="h-3.5 w-3.5" /> Applied
              </span>
            )}
            {available && typeof job.match_score !== "undefined" && (
              <MatchBadge score={job.match_score} basis={matchBasis} />
            )}
            {view !== "applied" && (
              <button
                type="button"
                onClick={() => onBookmark(job)}
                disabled={bookmarkSaving}
                className="bg-transparent border-0 cursor-pointer p-1.5 rounded-full hover:bg-[var(--ap-violet-soft)] transition-colors disabled:opacity-50"
                aria-label={isBookmarked ? "Remove saved job" : "Save job"}
                data-testid={`bookmark-btn-${String(job.title || "job").slice(0, 10)}`}
              >
                {bookmarkSaving ? (
                  <Loader2 className="w-5 h-5 animate-spin text-[var(--ap-violet)]" />
                ) : isBookmarked ? (
                  <BookmarkCheck className="w-5 h-5 text-[var(--ap-violet)]" />
                ) : (
                  <Bookmark className="w-5 h-5 text-[var(--ap-muted)]" />
                )}
              </button>
            )}
          </div>
        </div>

        {job.attribution_text && (
          attribution ? (
            <a
              href={attribution}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-2 text-[11px] text-[var(--ap-muted)] underline underline-offset-2 hover:text-[var(--ap-violet)]"
              data-testid="job-attribution"
            >
              {job.attribution_text}
            </a>
          ) : (
            <span className="inline-block mt-2 text-[11px] text-[var(--ap-muted)]" data-testid="job-attribution">
              {job.attribution_text}
            </span>
          )
        )}

        {expanded && (
          <div id={detailId} className="mt-4 pt-4 border-t border-[var(--ap-line)] space-y-4 ap-rise">
            {available && job.description ? (
              <div
                className="text-[var(--ap-muted)] text-[13px] leading-relaxed"
                dangerouslySetInnerHTML={{ __html: cleanDescription(job.description) }}
              />
            ) : (
              <p className="text-[var(--ap-muted)] text-[13px] leading-relaxed">
                {available
                  ? "Open the provider listing for the full job description."
                  : "This listing is no longer displayable under the provider's current status or policy. Your tracking record is preserved."}
              </p>
            )}

            {available && extractSkills(job.description).length > 0 && (
              <div className="flex flex-wrap gap-2" aria-label="Skills found in listing">
                {extractSkills(job.description).map((skill) => (
                  <span key={skill} className="ap-chip" style={{ fontSize: 11, padding: "4px 11px", background: "var(--ap-violet-soft)", borderColor: "transparent", color: "var(--ap-violet)" }}>
                    {skill}
                  </span>
                ))}
              </div>
            )}

            <div className="flex gap-2 flex-wrap items-center">
              {destination && (
                <a
                  href={destination}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ap-btn ap-btn-pri ap-btn-md no-underline"
                  style={{ fontSize: 13, padding: "9px 16px" }}
                  data-testid="view-job-btn"
                >
                  <ExternalLink className="w-4 h-4" /> {firstParty ? "Apply on company site" : "Apply on provider"}
                </a>
              )}

              {available && nativePlacementOSApply && view !== "applied" && (
                <button
                  type="button"
                  onClick={() => onNativeApply(job)}
                  className="ap-btn ap-btn-pri ap-btn-md"
                  style={{ fontSize: 13, padding: "9px 16px" }}
                  data-testid="placementos-apply-btn"
                >
                  <FileText className="w-4 h-4" /> Apply with PlacementOS
                </button>
              )}

              {destination && view !== "applied" && (
                <button
                  type="button"
                  onClick={() => onMarkApplied(job)}
                  disabled={markingApplied}
                  className="ap-btn ap-btn-ghost disabled:opacity-60"
                  style={{ fontSize: 13, padding: "9px 16px", color: "var(--ap-green)", borderColor: "rgba(22,163,74,.3)" }}
                  data-testid="mark-applied-btn"
                >
                  {markingApplied ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4" /> I submitted this application</>
                  )}
                </button>
              )}

              {available && view !== "applied" && user && hasSeasonPass === true && (
                <button
                  type="button"
                  onClick={() => onOpenApplyKit(job)}
                  className="ap-btn ap-btn-ghost"
                  style={{ fontSize: 13, padding: "9px 16px", color: "var(--ap-violet)", borderColor: "rgba(91,61,245,.3)" }}
                  data-testid="apply-kit-btn"
                >
                  <Sparkles className="w-4 h-4" /> Build Apply Kit
                </button>
              )}

              {available && view !== "applied" && hasSeasonPass === false && (
                <button
                  type="button"
                  onClick={() => onOpenApplyKit(job)}
                  className="ap-btn"
                  style={{ fontSize: 13, padding: "9px 16px", background: "var(--ap-canvas-2)", color: "var(--ap-muted)", border: "1px solid var(--ap-line)" }}
                  data-testid="locked-ai-btn"
                >
                  <Lock className="w-4 h-4" /> Apply Kit (Full Career OS)
                </button>
              )}

              {view === "applied" && tracking.id && (
                <label className="flex items-center gap-2 text-xs text-[var(--ap-muted)]">
                  Status
                  <select
                    value={status}
                    disabled={applicationSaving}
                    onChange={(event) => onApplicationStatus(job, event.target.value)}
                    className="rounded-full border border-[var(--ap-line)] bg-white px-3 py-2 text-[var(--ap-ink)] disabled:opacity-60"
                    aria-label={`Application status for ${job.title || "job"}`}
                  >
                    {APPLICATION_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                  {applicationSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                </label>
              )}
            </div>

            {destination && view !== "applied" && (
              <p className="text-[11px] text-[var(--ap-muted)]">
                PlacementOS never submits on your behalf. Open the provider listing first, then confirm only after you finish there.
              </p>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

function PlacementOSApplyDialog({ job, user, getHeaders, onClose, onSubmitted }) {
  const dialogRef = useRef(null);
  const [form, setForm] = useState(null);
  const [resumes, setResumes] = useState([]);
  const [resumeId, setResumeId] = useState("");
  const [phone, setPhone] = useState("");
  const [coverNote, setCoverNote] = useState("");
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const jobId = job.direct_job_id || job.employer_requisition_id;
  const selectedResume = resumes.find((resume) => resume.resume_id === resumeId) || null;

  useEffect(() => {
    let active = true;
    Promise.all([
      axios.get(`${API}/jobs/placementos/${encodeURIComponent(jobId)}/application`, { headers: getHeaders() }),
      axios.get(`${API}/dashboard/analyses`, { headers: getHeaders() }),
    ]).then(([formResponse, analysesResponse]) => {
      if (!active) return;
      setForm(formResponse.data);
      const seen = new Set();
      const options = (analysesResponse.data.analyses || []).filter((analysis) => {
        if (!analysis.resume_id || seen.has(analysis.resume_id)) return false;
        seen.add(analysis.resume_id);
        return true;
      });
      setResumes(options);
      if (options.length) setResumeId(options[0].resume_id);
    }).catch((requestError) => {
      if (active) setError(requestError.response?.data?.detail || "This application form could not be loaded.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [getHeaders, jobId]);

  useEffect(() => {
    const onKeyDown = (event) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKeyDown); document.body.style.overflow = previousOverflow; };
  }, [onClose]);

  const submit = async (event) => {
    event.preventDefault();
    if (!resumeId) { setError("Choose a resume from your Resume Vault."); return; }
    setSaving(true);
    setError("");
    try {
      const response = await axios.post(`${API}/jobs/placementos/${encodeURIComponent(jobId)}/application`, {
        resume_id: resumeId,
        phone,
        cover_note: coverNote,
        answers: (form.questions || []).map((question) => ({ question_id: question.id, value: answers[question.id] ?? "" })),
      }, { headers: { ...getHeaders(), "Content-Type": "application/json" } });
      toast.success("Application submitted to the recruiter");
      onSubmitted(response.data.application);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Your application could not be submitted.");
    } finally {
      setSaving(false);
    }
  };

  const answerField = (question) => {
    const value = answers[question.id] ?? "";
    const setValue = (nextValue) => setAnswers((current) => ({ ...current, [question.id]: nextValue }));
    const selectOptions = question.question_type === "yes_no"
      ? [["yes", "Yes"], ["no", "No"]]
      : (question.options || []).map((option) => [option, option]);
    if (["yes_no", "single_choice"].includes(question.question_type)) return (
      <Select value={value} onValueChange={setValue} required={question.answer_required}>
        <SelectTrigger
          aria-label={`Answer: ${question.prompt}`}
          className="h-12 rounded-xl border-[var(--ap-line)] bg-[var(--ap-canvas)] px-4 text-sm font-semibold text-[var(--ap-ink)] shadow-none transition hover:border-[var(--ap-violet)]/40 focus:ring-2 focus:ring-[var(--ap-violet)]/20"
        >
          <SelectValue placeholder="Choose an answer" />
        </SelectTrigger>
        <SelectContent sideOffset={8} align="start" className="z-[130] max-h-64 rounded-2xl border-slate-200 bg-white p-1.5 text-slate-900 shadow-[0_24px_70px_rgba(15,23,42,0.18)] [&_[role=listbox]]:!h-auto">
          {selectOptions.map(([optionValue, label]) => (
            <SelectItem key={optionValue} value={optionValue} className="cursor-pointer rounded-xl py-3 pl-3 pr-9 font-semibold text-slate-800 focus:bg-violet-50 focus:text-violet-700">
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
    const common = { required: question.answer_required, value, onChange: (event) => setValue(event.target.value) };
    if (question.question_type === "number") return <Input {...common} type="number" min="0" className="h-12 rounded-xl border-[var(--ap-line)] bg-white" />;
    if (question.question_type === "long_text") return <Textarea {...common} maxLength={4000} className="min-h-28 rounded-xl border-[var(--ap-line)] bg-white p-3 text-sm" />;
    return <Input {...common} maxLength={500} className="h-12 rounded-xl border-[var(--ap-line)] bg-white" />;
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-950/50 p-0 backdrop-blur-md sm:items-center sm:p-6" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="placementos-apply-title" className="ap-root flex h-[100dvh] w-full max-w-4xl flex-col overflow-hidden bg-[var(--ap-canvas)] shadow-[0_30px_100px_rgba(15,23,42,0.35)] sm:h-auto sm:max-h-[min(90dvh,800px)] sm:rounded-[30px]">
        <div className="ap-mesh relative flex shrink-0 items-start justify-between gap-4 overflow-hidden border-b border-[var(--ap-line)] px-5 py-4 sm:px-7 sm:py-5">
          <div className="min-w-0"><span className="ap-eyebrow">PlacementOS Apply</span><h2 id="placementos-apply-title" className="ap-display mt-1 truncate text-xl font-extrabold text-[var(--ap-ink)] sm:text-2xl">Apply for {job.title}</h2><p className="mt-1 text-sm text-[var(--ap-muted)]">Your application goes directly to <span className="font-bold text-[var(--ap-ink-2)]">{job.company}</span>.</p></div>
          <button type="button" onClick={onClose} className="grid h-10 w-10 shrink-0 cursor-pointer place-items-center rounded-full border border-[var(--ap-line)] bg-white text-[var(--ap-muted)] shadow-sm transition hover:border-[var(--ap-violet)]/30 hover:text-[var(--ap-violet)]" aria-label="Close application"><X className="h-4 w-4" /></button>
        </div>
        {loading ? <div className="flex items-center justify-center gap-3 p-12 text-sm font-semibold text-[var(--ap-muted)]"><Loader2 className="h-5 w-5 animate-spin text-[var(--ap-violet)]" /> Preparing your application...</div> : error && !form ? <div className="p-7"><p className="rounded-xl bg-red-50 p-4 text-sm text-red-700" role="alert">{error}</p></div> : form?.already_applied ? <div className="p-10 text-center"><CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" /><h3 className="ap-display mt-4 text-xl font-extrabold text-[var(--ap-ink)]">You already applied</h3><p className="mt-2 text-sm text-[var(--ap-muted)]">Track this role from the Applied tab.</p><button type="button" onClick={onClose} className="ap-btn ap-btn-pri mt-6 px-5 py-3">Done</button></div> : (
          <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
            <div data-testid="placementos-apply-scroll-region" className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-5 py-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:px-7 sm:py-6">
              <div className="grid min-w-0 gap-6 lg:grid-cols-[0.9fr_1.1fr]">
                <div className="grid min-w-0 content-start gap-5">
                  <div className="grid gap-4 rounded-2xl border border-[var(--ap-line)] bg-white p-4 sm:grid-cols-2">
                    <div className="min-w-0"><p className="text-[10px] font-extrabold uppercase tracking-wide text-[var(--ap-muted)]">Name</p><p className="mt-1 truncate text-sm font-bold text-[var(--ap-ink)]">{user?.name || "Not provided"}</p></div>
                    <div className="min-w-0"><p className="text-[10px] font-extrabold uppercase tracking-wide text-[var(--ap-muted)]">Email</p><p className="mt-1 truncate text-sm font-bold text-[var(--ap-ink)]">{user?.email}</p></div>
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-bold text-[var(--ap-ink-2)]">Resume from your Resume Vault</label>
                    {resumes.length ? (
                      <Select value={resumeId} onValueChange={setResumeId} required>
                        <SelectTrigger aria-label="Resume from your Resume Vault" data-testid="resume-vault-select" className="h-auto min-h-16 rounded-2xl border-[var(--ap-line)] bg-white px-3 py-2.5 text-left shadow-sm transition hover:border-[var(--ap-violet)]/40 focus:ring-2 focus:ring-[var(--ap-violet)]/20">
                          <span className="flex min-w-0 items-center gap-3">
                            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[var(--ap-violet-soft)] text-[var(--ap-violet)]"><FileText className="h-4 w-4" /></span>
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-extrabold text-[var(--ap-ink)]">{selectedResume?.filename || "Choose a resume"}</span>
                              <span className="mt-0.5 block truncate text-xs text-[var(--ap-muted)]">{selectedResume?.target_role || "General resume"}</span>
                            </span>
                          </span>
                        </SelectTrigger>
                        <SelectContent sideOffset={8} align="start" className="z-[130] max-h-72 rounded-2xl border-slate-200 bg-white p-1.5 text-slate-900 shadow-[0_24px_70px_rgba(15,23,42,0.18)] [&_[role=listbox]]:!h-auto">
                          {resumes.map((resume) => (
                            <SelectItem key={resume.resume_id} value={resume.resume_id} textValue={`${resume.filename || "Resume"} ${resume.target_role || "General resume"}`} className="cursor-pointer rounded-xl py-2.5 pl-2.5 pr-9 text-slate-900 focus:bg-violet-50 focus:text-violet-700">
                              <span className="block min-w-0 pr-2">
                                <span className="block truncate text-sm font-bold">{resume.filename || "Resume"}</span>
                                <span className="mt-0.5 block truncate text-xs font-normal text-slate-500">{resume.target_role || "General resume"}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">Analyze a resume in your Resume Vault before applying.</div>}
                  </div>
                  <div><label className="mb-2 flex justify-between text-xs font-bold text-[var(--ap-ink-2)]"><span>Phone number</span><span className="font-medium text-[var(--ap-muted)]">Optional</span></label><Input value={phone} onChange={(event) => setPhone(event.target.value)} maxLength={40} autoComplete="tel" placeholder="Your contact number" className="h-12 rounded-xl border-[var(--ap-line)] bg-white" /></div>
                  <div><label className="mb-2 flex justify-between text-xs font-bold text-[var(--ap-ink-2)]"><span>Note to the recruiter</span><span className="font-medium text-[var(--ap-muted)]">Optional</span></label><Textarea value={coverNote} onChange={(event) => setCoverNote(event.target.value)} maxLength={4000} placeholder="Briefly explain why this role interests you." className="min-h-24 resize-none rounded-xl border-[var(--ap-line)] bg-white p-3 text-sm" /></div>
                </div>
                <div className="min-w-0">
                  {(form?.questions || []).length > 0 ? <div><p className="text-sm font-extrabold text-[var(--ap-ink)]">Screening questions</p><p className="mt-1 text-xs leading-relaxed text-[var(--ap-muted)]">Answer honestly. The recruiter reviews every submission and makes the hiring decision.</p><div className="mt-4 grid gap-4">{form.questions.map((question, index) => <div key={question.id} className="rounded-2xl border border-[var(--ap-line)] bg-white p-4 shadow-sm"><label className="mb-3 block text-sm font-bold leading-relaxed text-[var(--ap-ink)]"><span className="mr-2 text-[var(--ap-violet)]">{index + 1}.</span>{question.prompt}{question.answer_required && <span className="ml-1 text-red-500">*</span>}</label>{answerField(question)}<p className="mt-2 text-[10px] font-bold uppercase tracking-wide text-[var(--ap-muted)]">{question.requirement === "must_have" ? "Must-have qualification" : question.requirement === "preferred" ? "Preferred qualification" : "Additional information"}</p></div>)}</div></div> : <div className="grid min-h-44 place-items-center rounded-2xl border border-dashed border-[var(--ap-line)] bg-white/70 p-6 text-center"><div><CheckCircle2 className="mx-auto h-8 w-8 text-[var(--ap-green)]" /><p className="mt-3 text-sm font-extrabold text-[var(--ap-ink)]">No screening questions</p><p className="mt-1 text-xs text-[var(--ap-muted)]">You can submit after reviewing your resume and contact details.</p></div></div>}
                </div>
              </div>
              {error && <p className="mt-5 rounded-xl bg-red-50 p-4 text-sm text-red-700" role="alert">{error}</p>}
            </div>
            <div className="shrink-0 border-t border-[var(--ap-line)] bg-white px-5 py-4 sm:px-7">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="max-w-xl text-[11px] leading-relaxed text-[var(--ap-muted)]">By submitting, you share this resume, contact information, and your answers with the verified recruiter. PlacementOS never submits automatically.</p>
                <button type="submit" disabled={saving || !resumeId} className="ap-btn ap-btn-fire min-h-12 shrink-0 px-5 disabled:opacity-60">{saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</> : <><CheckCircle2 className="h-4 w-4" /> Submit application</>}</button>
              </div>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}

function ApplyKitDialog({
  job,
  analyses,
  analysesLoading,
  getHeaders,
  requestIdsRef,
  onClose,
  sessionMarket,
  sessionMarketReady,
}) {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const initialJobDescription = useMemo(
    () => applyKitDescriptionForJob(job),
    [job],
  );
  const [analysisId, setAnalysisId] = useState("");
  const [jobDescription, setJobDescription] = useState(initialJobDescription);
  const [descriptionOrigin, setDescriptionOrigin] = useState(
    initialJobDescription ? "catalog_reviewed" : "user_paste",
  );
  const [loadingAction, setLoadingAction] = useState("");
  const [coverLetter, setCoverLetter] = useState("");
  const [tailored, setTailored] = useState(null);
  const [artifactOwner, setArtifactOwner] = useState("");
  const [artifactLoading, setArtifactLoading] = useState(false);
  const [analysisMarkets, setAnalysisMarkets] = useState({});
  const selectedAnalysis = useMemo(
    () => analyses.find((analysis) => analysis.id === analysisId) || null,
    [analyses, analysisId],
  );
  const hasFetchedAnalysisMarket = Boolean(analysisId)
    && Object.prototype.hasOwnProperty.call(analysisMarkets, analysisId);
  const selectedAnalysisMarket = String(
    selectedAnalysis?.market || analysisMarkets[analysisId] || "",
  ).trim().toUpperCase();
  const { cfg: selectedMarket, ready: selectedMarketReady } = usePricingForMarket(
    selectedAnalysisMarket || sessionMarket?.market,
    sessionMarket,
    sessionMarketReady,
  );
  const documentTermReady = Boolean(selectedAnalysisMarket) && selectedMarketReady;
  const documentTerm = documentTermReady
    ? (selectedMarket.doc_term === "CV" ? "CV" : "resume")
    : "document";
  const documentTermTitle = documentTerm === "CV"
    ? "CV"
    : `${documentTerm.charAt(0).toUpperCase()}${documentTerm.slice(1)}`;

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    setAnalysisId((current) => current || analyses[0]?.id || "");
  }, [analyses]);

  useEffect(() => {
    if (!analysisId || selectedAnalysis?.market || hasFetchedAnalysisMarket) {
      return undefined;
    }
    const controller = new AbortController();
    axios.get(`${API}/analysis/${analysisId}`, {
      headers: getHeaders(),
      signal: controller.signal,
    }).then((response) => {
      const resolvedMarket = String(response.data?.market || "").trim().toUpperCase();
      if (resolvedMarket) {
        setAnalysisMarkets((current) => ({
          ...current,
          [analysisId]: resolvedMarket,
        }));
      }
    }).catch((error) => {
      if (error.code !== "ERR_CANCELED") {
        setAnalysisMarkets((current) => ({ ...current, [analysisId]: null }));
      }
    });
    return () => controller.abort();
  }, [analysisId, selectedAnalysis?.market, hasFetchedAnalysisMarket, getHeaders]);

  useEffect(() => {
    restoreFocusRef.current = document.activeElement;
    const dialog = dialogRef.current;
    const focusableSelector = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      '[tabindex]:not([tabindex="-1"])',
    ].join(",");

    const focusCloseButton = () => {
      if (closeRef.current) closeRef.current.focus();
      else dialog?.focus();
    };
    focusCloseButton();

    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = Array.from(dialogRef.current.querySelectorAll(focusableSelector))
        .filter((element) => element.getAttribute("aria-hidden") !== "true");
      if (!focusable.length) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;
      if (event.shiftKey && (current === first || !dialogRef.current.contains(current))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (current === last || !dialogRef.current.contains(current))) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const restoreTarget = restoreFocusRef.current;
      if (restoreTarget?.isConnected && typeof restoreTarget.focus === "function") {
        restoreTarget.focus();
      }
    };
  }, []);

  useEffect(() => {
    // Artifacts belong to one resume/job pair. Clear them before fetching the
    // new pair so a slow or failed lookup can never leave another resume's
    // output visible under the current selector.
    setCoverLetter("");
    setTailored(null);
    setArtifactOwner(analysisId);
    if (!analysisId || !job?.url) {
      setArtifactLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    const requestedAnalysisId = analysisId;
    setArtifactLoading(true);
    axios.get(`${API}/jobs/apply-kit`, {
      headers: getHeaders(),
      params: { analysis_id: requestedAnalysisId, job_url: job.url },
      signal: controller.signal,
    }).then((response) => {
      if (response.data.analysis_id && response.data.analysis_id !== requestedAnalysisId) return;
      setArtifactOwner(requestedAnalysisId);
      setCoverLetter(response.data.cover_letter || "");
      setTailored(response.data.tailored_resume ? {
        ...response.data.tailored_resume,
        job_variant: response.data.job_variant,
        analysis_id: response.data.analysis_id,
      } : null);
    }).catch((error) => {
      if (error.code !== "ERR_CANCELED") toast.error("Could not load saved Apply Kit artifacts");
    }).finally(() => {
      if (!controller.signal.aborted) setArtifactLoading(false);
    });
    return () => controller.abort();
  }, [analysisId, job, getHeaders]);

  const generate = async (action) => {
    if (artifactLoading) {
      toast.info("Wait for saved Apply Kit artifacts to finish loading");
      return;
    }
    if (!analysisId) {
      toast.error("Choose a resume first");
      return;
    }
    if (jobDescription.trim().length < 80) {
      toast.error("Paste enough of the job description to tailor against it safely");
      return;
    }
    const normalizedDescription = jobDescription.trim();
    const intentFingerprint = [
      action,
      analysisId,
      String(job?.job_id || job?.url || ""),
      descriptionOrigin,
      normalizedDescription,
    ].join("\u0000");
    let requestEntry = requestIdsRef.current[action];
    if (!requestEntry || requestEntry.fingerprint !== intentFingerprint) {
      requestEntry = { fingerprint: intentFingerprint, requestId: requestUuid() };
      requestIdsRef.current[action] = requestEntry;
    }

    setLoadingAction(action);
    try {
      const response = await axios.post(
        `${API}/jobs/${action === "cover" ? "cover-letter" : "optimize-resume"}`,
        {
          job_id: job.job_id || "",
          job_url: job.url,
          job_title: job.title,
          company: job.company,
          job_description: normalizedDescription,
          analysis_id: analysisId,
          request_id: requestEntry.requestId,
          description_origin: descriptionOrigin,
          description_reviewed: true,
          user_supplied_description: descriptionOrigin === "user_paste",
        },
        { headers: { ...getHeaders(), "Content-Type": "application/json" } },
      );
      if (action === "cover") {
        setArtifactOwner(analysisId);
        setCoverLetter(response.data.cover_letter || "");
        toast.success(response.data.replayed ? "Saved cover letter loaded" : "Cover letter generated and saved");
      } else {
        setArtifactOwner(analysisId);
        setTailored({
          score: response.data.score,
          score_verified: response.data.score_verified,
          job_variant: response.data.job_variant,
          analysis_id: response.data.analysis_id,
        });
        toast.success(response.data.replayed ? "Saved tailored resume loaded" : "Tailored resume verified and saved");
      }
    } catch (error) {
      const status = error.response?.status;
      const detail = String(error.response?.data?.detail || "").toLowerCase();
      const confirmedTerminalFailure = status === 422
        || status === 502
        || (status === 409 && (
          detail.includes("start again")
          || detail.includes("new request")
        ));
      if (
        confirmedTerminalFailure
        && requestIdsRef.current[action]?.requestId === requestEntry.requestId
      ) {
        delete requestIdsRef.current[action];
      }
      toast.error(error.response?.data?.detail || "Could not generate this Apply Kit artifact");
    } finally {
      setLoadingAction("");
    }
  };

  const artifactsMatchSelection = artifactOwner === analysisId;
  const editorUrl = artifactsMatchSelection && tailored?.job_variant
    ? `/templates?analysis_id=${encodeURIComponent(tailored.analysis_id || analysisId)}&job_variant=${encodeURIComponent(tailored.job_variant)}&edit=1`
    : "";
  const descriptionLength = jobDescription.trim().length;
  const descriptionReady = descriptionLength >= 80;
  const generationBlocker = !analysisId
    ? `Choose a ${documentTerm} before creating an Apply Kit.`
    : !descriptionReady
      ? "Add at least 80 characters of responsibilities and requirements."
      : "";
  const generationDisabled = Boolean(loadingAction)
    || artifactLoading
    || Boolean(generationBlocker);
  const listingDescriptionAvailable = Boolean(job?.description);
  const listingDescriptionLoaded = Boolean(initialJobDescription);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--ap-ink)]/70 backdrop-blur-sm p-3 md:p-5"
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <section
        ref={dialogRef}
        className="ap-card w-full max-w-3xl max-h-[92vh] overflow-y-auto p-5 md:p-7"
        role="dialog"
        aria-modal="true"
        aria-labelledby="apply-kit-title"
        tabIndex={-1}
        data-testid="apply-kit-modal"
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <span className="ap-eyebrow">Assisted application</span>
            <h2 id="apply-kit-title" className="ap-display text-xl font-bold text-[var(--ap-ink)] mt-1">Apply Kit for {job.title}</h2>
            <p className="text-sm text-[var(--ap-muted)] mt-1">{job.company}</p>
          </div>
          <button ref={closeRef} type="button" onClick={onClose} className="p-2 rounded-full hover:bg-[var(--ap-canvas-2)]" aria-label="Close Apply Kit">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-5">
          <div>
            <label htmlFor="apply-kit-resume" className="block text-sm font-semibold text-[var(--ap-ink)] mb-2">1. Choose the {documentTerm} to use</label>
            {analysesLoading ? (
              <div className="flex items-center gap-2 text-sm text-[var(--ap-muted)]"><Loader2 className="w-4 h-4 animate-spin" /> Loading {documentTermTitle} Vault...</div>
            ) : analyses.length ? (
              <select
                id="apply-kit-resume"
                value={analysisId}
                onChange={(event) => setAnalysisId(event.target.value)}
                disabled={Boolean(loadingAction)}
                className="w-full rounded-xl border border-[var(--ap-line)] bg-white px-4 py-3 text-sm text-[var(--ap-ink)]"
              >
                {analyses.map((analysis) => (
                  <option key={analysis.id} value={analysis.id}>
                    {analysis.filename || analysis.target_role || documentTermTitle} · score {analysis.improved_score || analysis.score || "—"}
                  </option>
                ))}
              </select>
            ) : (
              <p className="rounded-xl bg-[var(--ap-canvas-2)] p-4 text-sm text-[var(--ap-muted)]">Upload and analyze a {documentTerm} in your {documentTermTitle} Vault before building an Apply Kit.</p>
            )}
          </div>

          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <label htmlFor="apply-kit-description" className="block text-sm font-semibold text-[var(--ap-ink)]">
                2. {listingDescriptionLoaded ? "Review" : "Add"} the job description
              </label>
              {listingDescriptionLoaded && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700" data-testid="apply-kit-prefill-status">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Listing text loaded
                </span>
              )}
            </div>
            <textarea
              id="apply-kit-description"
              value={jobDescription}
              onChange={(event) => {
                const nextDescription = event.target.value;
                setJobDescription(nextDescription);
                setDescriptionOrigin(
                  initialJobDescription
                    && nextDescription.trim() === initialJobDescription.trim()
                    ? "catalog_reviewed"
                    : "user_paste",
                );
              }}
              disabled={Boolean(loadingAction)}
              rows={8}
              maxLength={100000}
              placeholder={
                listingDescriptionLoaded
                  ? "Review or edit the job description for this application..."
                  : listingDescriptionAvailable
                  ? "Automatic AI reuse is not enabled for this provider. Paste the role text you reviewed..."
                  : "Paste the role responsibilities and requirements from the original listing..."
              }
              aria-describedby="apply-kit-description-help apply-kit-description-count"
              aria-invalid={jobDescription.length > 0 && !descriptionReady}
              className="w-full rounded-xl border border-[var(--ap-line)] bg-white px-4 py-3 text-sm leading-relaxed text-[var(--ap-ink)] resize-y disabled:cursor-wait disabled:bg-[var(--ap-canvas-2)]"
            />
            <div className="mt-2 flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
              <p id="apply-kit-description-help" className="text-[11px] leading-relaxed text-[var(--ap-muted)]">
                {listingDescriptionLoaded
                  ? "Auto-filled from this listing. Review or edit it before generating; only a hash—not the raw description—is stored with the artifacts."
                  : "Review the role text before generating. Only a hash—not the raw description—is stored with the artifacts."}
              </p>
              <span
                id="apply-kit-description-count"
                className={`shrink-0 text-[11px] font-bold ${descriptionReady ? "text-emerald-700" : "text-[var(--ap-muted)]"}`}
              >
                {descriptionLength.toLocaleString()} / 100,000
              </span>
            </div>
          </div>

          <div className="ap-apply-kit-actions" data-testid="apply-kit-actions">
            <div className="ap-apply-kit-actions-head">
              <div>
                <p className="text-sm font-semibold text-[var(--ap-ink)]">3. Create your application assets</p>
                <p className="mt-0.5 text-[11px] leading-relaxed text-[var(--ap-muted)]">You review every generated claim before using it.</p>
              </div>
            </div>
            <div className="ap-apply-kit-actions-grid">
              <button
                type="button"
                disabled={generationDisabled}
                onClick={() => generate("cover")}
                className="ap-btn ap-btn-pri ap-apply-kit-action"
                aria-busy={loadingAction === "cover"}
              >
                <span className="ap-apply-kit-action-icon" aria-hidden="true">
                  {loadingAction === "cover" ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
                </span>
                <span className="ap-apply-kit-action-copy">
                  <span>{loadingAction === "cover" ? "Generating cover letter…" : "Generate cover letter"}</span>
                  <span>Drafted from your selected resume</span>
                </span>
              </button>
              <button
                type="button"
                disabled={generationDisabled}
                onClick={() => generate("resume")}
                className="ap-btn ap-btn-ghost ap-apply-kit-action ap-apply-kit-action-secondary"
                aria-busy={loadingAction === "resume"}
              >
                <span className="ap-apply-kit-action-icon" aria-hidden="true">
                  {loadingAction === "resume" ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
                </span>
                <span className="ap-apply-kit-action-copy">
                  <span>{loadingAction === "resume" ? `Tailoring ${documentTerm}…` : `Tailor ${documentTerm}`}</span>
                  <span>Creates a verified job-specific version</span>
                </span>
              </button>
            </div>
            {generationBlocker && (
              <p className="ap-apply-kit-action-note" role="status">{generationBlocker}</p>
            )}
          </div>

          {artifactLoading && <div className="flex items-center gap-2 text-sm text-[var(--ap-muted)]"><Loader2 className="w-4 h-4 animate-spin" /> Loading saved artifacts...</div>}

          {artifactsMatchSelection && coverLetter && (
            <div className="rounded-2xl border border-[var(--ap-line)] p-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <h3 className="font-semibold text-[var(--ap-ink)]">Saved cover letter</h3>
                <button
                  type="button"
                  onClick={async () => {
                    try { await navigator.clipboard.writeText(coverLetter); toast.success("Cover letter copied"); }
                    catch { toast.error("Clipboard access was blocked"); }
                  }}
                  className="ap-btn ap-btn-ghost"
                  style={{ fontSize: 12, padding: "7px 12px" }}
                >
                  <Clipboard className="w-4 h-4" /> Copy
                </button>
              </div>
              <textarea
                value={coverLetter}
                onChange={(event) => setCoverLetter(event.target.value)}
                rows={12}
                className="w-full rounded-xl border border-[var(--ap-line)] bg-white px-4 py-3 text-sm text-[var(--ap-ink)] resize-y"
                aria-label="Generated cover letter"
              />
              <p className="text-[11px] text-[var(--ap-muted)] mt-2">Review every claim before using it. Edits here are a local working copy; copy them when ready.</p>
            </div>
          )}

          {artifactsMatchSelection && tailored && (
            <div className="rounded-2xl border border-[var(--ap-line)] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-[var(--ap-ink)]">Tailored {documentTerm} saved</h3>
                <p className="text-sm text-[var(--ap-muted)]">
                  {tailored.score_verified ? `Verified PlacementOS score: ${tailored.score}` : "Score verification pending"}
                </p>
              </div>
              {editorUrl && <a href={editorUrl} className="ap-btn ap-btn-pri no-underline">Review and export</a>}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default function JobFeedPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { cfg: market, ready: marketReady } = useMarketConfig();
  const { getHeaders, user, loading: authLoading } = useAuth();
  const requestSequence = useRef(0);
  const retainedJobQueryRef = useRef(null);
  const requestedView = searchParams.get("view");
  const view = VIEWS.some(({ value }) => value === requestedView) ? requestedView : "discover";
  const keywords = String(searchParams.get("keywords") || "").slice(0, 200).trim();
  const requestedLocation = String(searchParams.get("location") || "").slice(0, 120).trim();
  const location = requestedLocation || "All Locations";
  const sortBy = searchParams.get("sort") === "date" ? "date" : "match";
  const useOptimized = searchParams.get("source") === "optimized";
  const page = parseJobPage(searchParams.get("page"));
  const activeViewRef = useRef(view);
  activeViewRef.current = view;

  const [searchInput, setSearchInput] = useState(keywords);
  const [showFilters, setShowFilters] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const [totalHits, setTotalHits] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [matchBasis, setMatchBasis] = useState("role_and_keywords");
  const [rankingCapped, setRankingCapped] = useState(false);
  const [matchSourceNotice, setMatchSourceNotice] = useState("");
  const [bookmarks, setBookmarks] = useState({});
  const [bookmarkSaving, setBookmarkSaving] = useState({});
  const [applicationsSaving, setApplicationsSaving] = useState({});
  const [statusSaving, setStatusSaving] = useState({});
  const [hasSeasonPass, setHasSeasonPass] = useState(null);
  const [applyKitJob, setApplyKitJob] = useState(null);
  const [nativeApplyJob, setNativeApplyJob] = useState(null);
  // Lives above the dialog so closing and reopening after an ambiguous network
  // outcome still reuses the same generation retry token.
  const applyKitRequestIdsRef = useRef({});
  const [analyses, setAnalyses] = useState([]);
  const [analysesLoading, setAnalysesLoading] = useState(false);

  const locationOptions = useMemo(() => {
    const options = ["All Locations"];
    // Never render the India safety fallback as if it were the resolved
    // account market. The server-provided name becomes selectable only after
    // this identity's market-config request has completed.
    if (marketReady && market.name) options.push(market.name);
    options.push("Remote");
    return options;
  }, [market.name, marketReady]);

  const canonicalSearchParams = useMemo(() => buildJobSearchParams({
    view,
    keywords,
    location,
    sortBy,
    useOptimized,
    page,
  }), [view, keywords, location, sortBy, useOptimized, page]);

  // The URL is the committed filter state. This makes browser back/forward a
  // real navigation instead of having a one-way state effect overwrite it.
  useEffect(() => {
    if (canonicalSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(canonicalSearchParams, { replace: true });
    }
  }, [canonicalSearchParams, searchParams, setSearchParams]);

  useEffect(() => {
    setSearchInput(keywords);
  }, [keywords]);

  const updateJobQuery = useCallback((updates, { replace = false } = {}) => {
    const next = buildJobSearchParams({
      view,
      keywords,
      location,
      sortBy,
      useOptimized,
      page,
      ...updates,
    });
    if (next.toString() === canonicalSearchParams.toString()) return;
    setSearchParams(next, { replace });
  }, [view, keywords, location, sortBy, useOptimized, page, canonicalSearchParams, setSearchParams]);

  useEffect(() => {
    if (authLoading) return undefined;
    if (!user) {
      setHasSeasonPass(false);
      return undefined;
    }
    let active = true;
    setHasSeasonPass(null);
    axios.get(`${API}/user/subscription`, { headers: getHeaders() }).then((response) => {
      if (!active) return;
      setHasSeasonPass(Boolean(response.data.has_season_pass));
    }).catch(() => {
      if (active) setHasSeasonPass(null);
    });
    return () => { active = false; };
  }, [authLoading, user, getHeaders]);

  useEffect(() => {
    if (!applyKitJob || !user) return undefined;
    let active = true;
    setAnalysesLoading(true);
    axios.get(`${API}/dashboard/analyses`, { headers: getHeaders() }).then((response) => {
      if (active) setAnalyses(response.data.analyses || []);
    }).catch(() => {
      if (active) { setAnalyses([]); toast.error("Could not load your Resume Vault"); }
    }).finally(() => { if (active) setAnalysesLoading(false); });
    return () => { active = false; };
  }, [applyKitJob, user, getHeaders]);

  const fetchBookmarkRefs = useCallback(async (signal) => {
    const refs = {};
    let bookmarkPage = 1;
    let pages = 1;
    do {
      const response = await axios.get(`${API}/jobs/bookmarks`, {
        headers: getHeaders(),
        params: { page: bookmarkPage, page_size: 100 },
        signal,
      });
      for (const bookmark of response.data.bookmarks || []) {
        refs[`url:${bookmark.job_url}`] = true;
        if (bookmark.job_id) refs[`id:${bookmark.job_id}`] = true;
      }
      pages = Number(response.data.total_pages) || 1;
      bookmarkPage += 1;
    } while (bookmarkPage <= pages);
    return refs;
  }, [getHeaders]);

  useEffect(() => {
    if (authLoading) return undefined;
    const retainedQuery = retainedJobQueryRef.current;
    if (
      retainedQuery
      && retainedQuery.query === canonicalSearchParams.toString()
      && retainedQuery.reloadToken === reloadToken
      && retainedQuery.userId === String(user?.id || "")
    ) {
      setLoading(false);
      return undefined;
    }
    retainedJobQueryRef.current = null;
    if (view === "applied" && !user) {
      setJobs([]);
      setTotalHits(0);
      setTotalPages(0);
      setLoading(false);
      setError("");
      return undefined;
    }

    const controller = new AbortController();
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError("");

    const run = async () => {
      let response;
      let downgradedToStandard = false;
      if (view === "discover") {
        const params = { page, page_size: 20, sort: sortBy };
        if (keywords) params.keywords = keywords;
        if (location !== "All Locations") params.location = location;
        if (useOptimized) params.source = "optimized";
        response = await axios.get(`${API}/jobs`, {
          headers: getHeaders(), params, signal: controller.signal,
        });
      } else if (view === "saved") {
        response = await axios.get(`${API}/jobs/bookmarks`, {
          headers: getHeaders(), params: { page, page_size: 20 }, signal: controller.signal,
        });
      } else {
        response = await axios.get(`${API}/jobs/applications`, {
          headers: getHeaders(), params: { page, page_size: 20 }, signal: controller.signal,
        });
      }
      if (sequence !== requestSequence.current) return;

      if (view === "discover") {
        const effectiveSource = String(response.data.source || "standard");
        if (useOptimized && effectiveSource !== "optimized") {
          setMatchSourceNotice(user
            ? "No optimized resume is available for this match, so these jobs use your available resume and role context."
            : "Optimized resume matching requires a signed-in account with an optimized resume. These jobs use role-based matching.");
          downgradedToStandard = true;
        } else if (useOptimized) {
          setMatchSourceNotice("");
        }

        const responsePages = Number(response.data.pages) || 0;
        const lastAvailablePage = Math.max(1, responsePages);
        if (page > lastAvailablePage) {
          updateJobQuery({ page: lastAvailablePage }, { replace: true });
          return;
        }
        setJobs((response.data.jobs || []).map((job) => ({ ...job, _available: true })));
        setTotalHits(Number(response.data.hits) || 0);
        setTotalPages(responsePages);
        setMatchBasis(response.data.match_basis || "role_and_keywords");
        setRankingCapped(Boolean(response.data.ranking_capped));
        try { setBookmarks(await fetchBookmarkRefs(controller.signal)); }
        catch (bookmarkError) { if (bookmarkError.code !== "ERR_CANCELED") setBookmarks({}); }
        if (downgradedToStandard && !controller.signal.aborted && sequence === requestSequence.current) {
          const downgradedSearchParams = buildJobSearchParams({
            view,
            keywords,
            location,
            sortBy,
            useOptimized: false,
            page,
          });
          retainedJobQueryRef.current = {
            query: downgradedSearchParams.toString(),
            reloadToken,
            userId: String(user?.id || ""),
          };
          setSearchParams(downgradedSearchParams, { replace: true });
        }
      } else {
        const field = view === "saved" ? "bookmarks" : "applications";
        const rows = response.data[field] || [];
        const responsePages = Number(response.data.total_pages) || 0;
        const lastAvailablePage = Math.max(1, responsePages);
        if (page > lastAvailablePage) {
          updateJobQuery({ page: lastAvailablePage }, { replace: true });
          return;
        }
        setJobs(rows.map((row) => ({ ...(row.job || {}), _tracking: row, _available: row.available })));
        setTotalHits(Number(response.data.total) || 0);
        setTotalPages(responsePages);
        setRankingCapped(false);
        if (view === "saved") {
          const refs = {};
          for (const row of rows) {
            refs[`url:${row.job_url}`] = true;
            if (row.job_id) refs[`id:${row.job_id}`] = true;
          }
          setBookmarks(refs);
        }
      }
    };

    run().catch((requestError) => {
      if (requestError.code === "ERR_CANCELED" || sequence !== requestSequence.current) return;
      setJobs([]);
      setError(requestError.response?.data?.detail || `Could not load ${view === "discover" ? "jobs" : view}`);
    }).finally(() => {
      if (!controller.signal.aborted && sequence === requestSequence.current) setLoading(false);
    });
    return () => controller.abort();
  }, [authLoading, user, view, page, keywords, location, sortBy, useOptimized, reloadToken, canonicalSearchParams, getHeaders, fetchBookmarkRefs, updateJobQuery, setSearchParams]);

  const changeView = (nextView) => {
    activeViewRef.current = nextView;
    updateJobQuery({ view: nextView, page: 1 });
  };

  const handleSearch = (event) => {
    event.preventDefault();
    updateJobQuery({ keywords: searchInput.trim(), page: 1 });
  };

  const openApplyKit = (job) => {
    if (!user) {
      toast.info("Sign in to build an Apply Kit");
      navigate(jobSignInPath(searchParams));
      return;
    }
    if (hasSeasonPass === false) {
      navigate("/season-pass");
      return;
    }
    if (hasSeasonPass === null) {
      toast.info("Checking your Full Career OS access. Try again in a moment.");
      return;
    }
    setApplyKitJob(job);
  };

  const openNativeApply = (job) => {
    if (!user) {
      toast.info("Sign in to apply with PlacementOS");
      navigate(jobSignInPath(searchParams));
      return;
    }
    if (!job.direct_job_id) {
      toast.error("This application form is temporarily unavailable");
      return;
    }
    setNativeApplyJob(job);
  };

  const markApplied = async (job) => {
    if (!user) {
      toast.info("Sign in to track applications");
      navigate(jobSignInPath(searchParams));
      return;
    }
    if (!job.url) {
      toast.error("This job no longer has an application link");
      return;
    }
    const key = stableJobKey(job);
    if (applicationsSaving[key]) return;
    setApplicationsSaving((current) => ({ ...current, [key]: true }));
    try {
      await axios.post(`${API}/jobs/apply-status`, {
        job_url: job.url,
        status: "applied",
      }, { headers: { ...getHeaders(), "Content-Type": "application/json" } });
      // Reload whichever application-sensitive view is active. Discover then
      // excludes and backfills the committed role; Applied includes the new
      // tracker row. Never mutate a Saved list through a stale async closure.
      if (["discover", "applied"].includes(activeViewRef.current)) {
        setReloadToken((current) => current + 1);
      }
      toast.success("Application saved in your Applied tracker");
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || "Could not save this application");
    } finally {
      setApplicationsSaving((current) => {
        const next = { ...current }; delete next[key]; return next;
      });
    }
  };

  const toggleBookmark = async (job) => {
    const url = job._tracking?.job_url || job.url;
    if (!url) {
      toast.error("This listing can no longer be saved");
      return;
    }
    const key = stableJobKey(job) || url;
    if (bookmarkSaving[key]) return;
    const bookmarked = !(
      bookmarks[`url:${url}`]
      || (job.job_id && bookmarks[`id:${job.job_id}`])
    );
    setBookmarkSaving((current) => ({ ...current, [key]: true }));
    try {
      await axios.post(`${API}/jobs/bookmark`, {
        job_url: url,
        bookmarked,
      }, { headers: { ...getHeaders(), "Content-Type": "application/json" } });
      setBookmarks((current) => ({
        ...current,
        [`url:${url}`]: bookmarked,
        ...(job.job_id ? { [`id:${job.job_id}`]: bookmarked } : {}),
      }));
      if (activeViewRef.current === "saved") {
        // Re-read so pagination is backfilled and an emptied last page safely
        // moves to the preceding page through the response-page guard.
        setReloadToken((current) => current + 1);
      }
      toast.success(bookmarked ? "Job saved" : "Removed from Saved");
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || "Could not update Saved jobs");
    } finally {
      setBookmarkSaving((current) => {
        const next = { ...current }; delete next[key]; return next;
      });
    }
  };

  const updateApplicationStatus = async (job, status) => {
    const applicationId = job._tracking?.id;
    if (!applicationId || statusSaving[applicationId]) return;
    setStatusSaving((current) => ({ ...current, [applicationId]: true }));
    try {
      const response = await axios.patch(`${API}/jobs/applications/${encodeURIComponent(applicationId)}`, {
        status,
      }, { headers: { ...getHeaders(), "Content-Type": "application/json" } });
      if (activeViewRef.current === "applied") {
        setJobs((current) => current.map((item) => item._tracking?.id === applicationId
          ? { ...item, _tracking: { ...item._tracking, ...response.data.application } }
          : item));
      } else if (activeViewRef.current === "discover") {
        setReloadToken((current) => current + 1);
      }
      toast.success(status === "rejected" || status === "withdrawn"
        ? "Status updated; this role may appear in Discover again"
        : "Application status updated");
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || "Could not update application status");
    } finally {
      setStatusSaving((current) => {
        const next = { ...current }; delete next[applicationId]; return next;
      });
    }
  };

  const emptyCopy = view === "saved"
    ? ["No saved jobs yet", "Save roles from Discover and they will stay here across reloads."]
    : view === "applied"
      ? [user ? "No applications tracked yet" : "Sign in to view Applied jobs", user ? "Open a provider listing, submit there, then confirm it in PlacementOS." : "Your application tracker is private to your account."]
      : ["No jobs found", "Try different search terms or broaden your filters."];

  return (
    <div className="ap-root min-h-screen pt-24 pb-16" data-testid="job-feed-page">
      <div className="ap-section" style={{ maxWidth: 1040 }}>
        <div className="flex items-center gap-3 mb-6">
          <button type="button" onClick={() => navigate("/dashboard")} className="w-10 h-10 grid place-items-center rounded-full bg-white border border-[var(--ap-line)] text-[var(--ap-muted)] hover:text-[var(--ap-ink)]" aria-label="Back to dashboard" data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <span className="ap-eyebrow flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" /> Candidate jobs workspace</span>
            <h1 className="ap-display text-[28px] md:text-[34px] font-extrabold text-[var(--ap-ink)] leading-tight mt-1">Jobs that move with <span className="ap-grad-text">you</span></h1>
            <p className="text-[var(--ap-muted)] text-sm mt-0.5">
              {loading ? "Loading your jobs..." : `${totalHits.toLocaleString()} ${view === "discover" ? "matching roles" : view}`}
            </p>
          </div>
          {hasSeasonPass === false && (
            <button type="button" onClick={() => navigate("/season-pass")} className="ap-btn ap-btn-fire shrink-0 hidden sm:flex" data-testid="upgrade-btn">
              <Crown className="w-4 h-4" /> Full Career OS
            </button>
          )}
        </div>

        <nav className="flex gap-2 mb-5 overflow-x-auto" aria-label="Job workspace views">
          {VIEWS.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => changeView(item.value)}
              aria-current={view === item.value ? "page" : undefined}
              className="ap-chip cursor-pointer shrink-0"
              style={view === item.value ? { background: "var(--ap-violet)", borderColor: "transparent", color: "#fff" } : { color: "var(--ap-muted)" }}
            >
              {item.value === "saved" && <Bookmark className="w-3.5 h-3.5" />}
              {item.value === "applied" && <CheckCircle2 className="w-3.5 h-3.5" />}
              {item.label}
            </button>
          ))}
        </nav>

        {view === "discover" && (
          <form onSubmit={handleSearch} className="ap-card p-4 md:p-5 mb-6 space-y-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ap-muted)] z-10" />
                <Input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} maxLength={200} placeholder="Search roles, skills, or companies" className="pl-10 bg-[var(--ap-canvas)] border-[var(--ap-line)] text-[var(--ap-ink)] h-12 rounded-full" data-testid="search-input" />
              </div>
              <button type="submit" className="ap-btn ap-btn-pri shrink-0" style={{ padding: "0 22px" }} data-testid="search-btn">Search</button>
              <button type="button" onClick={() => setShowFilters((value) => !value)} className="ap-btn ap-btn-ghost sm:hidden shrink-0" style={{ padding: "0 14px" }} aria-expanded={showFilters} aria-label="Toggle filters"><Filter className="w-4 h-4" /></button>
            </div>

            <div className={`gap-2 flex-wrap items-center ${showFilters ? "flex" : "hidden sm:flex"}`}>
              {locationOptions.map((option) => {
                const selected = location === option || (option === "Remote" && location.toLowerCase().startsWith("remote"));
                return (
                  <button key={option} type="button" onClick={() => updateJobQuery({ location: option, page: 1 })} aria-pressed={selected} className="ap-chip cursor-pointer" style={selected ? { background: "var(--ap-violet)", borderColor: "transparent", color: "#fff" } : { color: "var(--ap-muted)" }}>
                    {option}
                  </button>
                );
              })}
              <div className="sm:ml-auto flex items-center gap-2 flex-wrap">
                {SORT_OPTIONS.map((option) => (
                  <button key={option.value} type="button" onClick={() => updateJobQuery({ sortBy: option.value, page: 1 })} aria-pressed={sortBy === option.value} className="ap-chip cursor-pointer" style={sortBy === option.value ? { background: "var(--ap-violet)", borderColor: "transparent", color: "#fff" } : { color: "var(--ap-muted)" }}>
                    {option.label}
                  </button>
                ))}
                <button type="button" onClick={() => { if (!user) { toast.info("Sign in to match your optimized resume"); navigate(jobSignInPath(searchParams)); return; } setMatchSourceNotice(""); updateJobQuery({ useOptimized: !useOptimized, page: 1 }); }} aria-pressed={useOptimized} className="ap-chip cursor-pointer" style={useOptimized ? { background: "var(--ap-fire)", borderColor: "transparent", color: "#fff" } : { color: "var(--ap-violet)" }} data-testid="optimized-resume-toggle">
                  <Sparkles className="w-3.5 h-3.5" /> {useOptimized ? "Optimized resume" : "Match optimized resume"}
                </button>
              </div>
            </div>
          </form>
        )}

        {matchSourceNotice && view === "discover" && (
          <div
            className="mb-4 flex items-start gap-3 rounded-2xl border border-[var(--ap-line)] bg-[var(--ap-violet-soft)] px-4 py-3 text-sm text-[var(--ap-muted)]"
            role="status"
            data-testid="match-source-fallback"
          >
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[var(--ap-violet)]" />
            <span className="flex-1">{matchSourceNotice}</span>
            <button
              type="button"
              onClick={() => setMatchSourceNotice("")}
              className="rounded-full bg-transparent p-1 text-[var(--ap-muted)] hover:bg-white hover:text-[var(--ap-ink)]"
              aria-label="Dismiss matching notice"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {rankingCapped && view === "discover" && (
          <p className="mb-4 text-xs text-[var(--ap-muted)]">Best-match ranking is calculated across the newest 200 eligible roles; refine your search to narrow it further.</p>
        )}

        {error ? (
          <div className="ap-card p-10 text-center" role="alert">
            <p className="font-semibold text-[var(--ap-ink)] mb-2">We could not load this view</p>
            <p className="text-sm text-[var(--ap-muted)] mb-5">{error}</p>
            <button type="button" onClick={() => setReloadToken((value) => value + 1)} className="ap-btn ap-btn-pri"><RefreshCw className="w-4 h-4" /> Retry</button>
          </div>
        ) : loading ? (
          <div className="space-y-3" aria-label="Loading jobs">
            {[0, 1, 2, 3].map((item) => <div key={item} className="ap-card ap-skeleton p-5" style={{ height: 120 }} />)}
          </div>
        ) : jobs.length === 0 ? (
          <div className="ap-card p-10 md:p-12 text-center">
            <div className="w-16 h-16 rounded-2xl grid place-items-center mx-auto mb-5" style={{ background: "var(--ap-violet-soft)" }}><Briefcase className="w-8 h-8 text-[var(--ap-violet)]" /></div>
            <p className="ap-display text-xl font-bold text-[var(--ap-ink)] mb-2">{emptyCopy[0]}</p>
            <p className="text-[var(--ap-muted)] text-sm mb-5">{emptyCopy[1]}</p>
            {view === "applied" && !user && <button type="button" onClick={() => navigate(jobSignInPath(searchParams))} className="ap-btn ap-btn-pri">Sign in</button>}
            {view !== "discover" && user && <button type="button" onClick={() => changeView("discover")} className="ap-btn ap-btn-ghost">Browse jobs</button>}
          </div>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => {
              const key = stableJobKey(job);
              const bookmarkUrl = job._tracking?.job_url || job.url;
              const isBookmarked = Boolean(
                bookmarks[`url:${bookmarkUrl}`]
                || (job.job_id && bookmarks[`id:${job.job_id}`])
              );
              return (
                <JobCard
                  key={key}
                  job={job}
                  view={view}
                  matchBasis={matchBasis}
                  isBookmarked={isBookmarked}
                  bookmarkSaving={Boolean(bookmarkSaving[key])}
                  onBookmark={toggleBookmark}
                  onMarkApplied={markApplied}
                  markingApplied={Boolean(applicationsSaving[key])}
                  onOpenApplyKit={openApplyKit}
                  onNativeApply={openNativeApply}
                  hasSeasonPass={hasSeasonPass}
                  user={user}
                  applicationSaving={Boolean(statusSaving[job._tracking?.id])}
                  onApplicationStatus={updateApplicationStatus}
                />
              );
            })}

            {view === "discover" && hasSeasonPass === false && jobs.length >= 10 && (
              <div className="ap-card ap-mesh overflow-hidden p-8 text-center" style={{ borderColor: "var(--ap-violet)" }} data-testid="job-limit-upsell">
                <Lock className="w-8 h-8 text-[var(--ap-violet)] mx-auto mb-3" />
                <h3 className="ap-display text-xl font-bold text-[var(--ap-ink)] mb-2">See the complete recent feed and build per-job Apply Kits</h3>
                <p className="text-[var(--ap-muted)] text-sm mb-5">You still review and submit every application yourself.</p>
                <button type="button" onClick={() => navigate("/season-pass")} className="ap-btn ap-btn-fire ap-btn-lg">
                  Full Career OS · {marketReady ? formatPrice(market, market.prices.pass_annual) : "…"}
                </button>
              </div>
            )}
          </div>
        )}

        {totalPages > 1 && !loading && !error && (
          <nav className="flex items-center justify-center gap-4 mt-8" aria-label="Job pages">
            <button type="button" onClick={() => updateJobQuery({ page: Math.max(1, page - 1) })} disabled={page <= 1} className="ap-btn ap-btn-ghost disabled:opacity-30"><ChevronLeft className="w-4 h-4" /> Previous</button>
            <span className="text-[var(--ap-muted)] text-sm font-semibold">Page {page} of {totalPages}</span>
            <button type="button" onClick={() => updateJobQuery({ page: Math.min(totalPages, page + 1) })} disabled={page >= totalPages} className="ap-btn ap-btn-ghost disabled:opacity-30">Next <ChevronRight className="w-4 h-4" /></button>
          </nav>
        )}
      </div>

      {applyKitJob && (
        <ApplyKitDialog
          key={`${stableJobKey(applyKitJob)}::${applyKitJob.url || ""}`}
          job={applyKitJob}
          analyses={analyses}
          analysesLoading={analysesLoading}
          getHeaders={getHeaders}
          requestIdsRef={applyKitRequestIdsRef}
          onClose={() => setApplyKitJob(null)}
          sessionMarket={market}
          sessionMarketReady={marketReady}
        />
      )}
      {nativeApplyJob && (
        <PlacementOSApplyDialog
          key={nativeApplyJob.direct_job_id}
          job={nativeApplyJob}
          user={user}
          getHeaders={getHeaders}
          onClose={() => setNativeApplyJob(null)}
          onSubmitted={() => {
            const submittedJobId = nativeApplyJob.direct_job_id;
            setNativeApplyJob(null);
            if (activeViewRef.current === "discover") {
              setJobs((current) => current.filter((item) => item.direct_job_id !== submittedJobId));
              setTotalHits((current) => Math.max(0, current - 1));
            }
            setReloadToken((current) => current + 1);
          }}
        />
      )}
    </div>
  );
}
